import { useAppSelector } from './hooks';
import { selectUserPermissions } from '../slices/authSlice';

export const usePermissions = () => {
  const permissions = useAppSelector(selectUserPermissions);

  // Debug permissions
  console.log('usePermissions - Available permissions:', permissions);

  const hasPermission = (permission) => {
    if (!permissions || permissions.length === 0) {
      console.log(`hasPermission(${permission}): No permissions available`);
      return false;
    }
    
    // Check for wildcard permission
    if (permissions.includes('*')) {
      console.log(`hasPermission(${permission}): Wildcard permission found`);
      return true;
    }
    
    // Check for exact permission match
    if (permissions.includes(permission)) {
      console.log(`hasPermission(${permission}): Exact match found`);
      return true;
    }
    
    // Check for client-specific permission match
    const clientPermission = `client_${permission}`;
    if (permissions.includes(clientPermission)) {
      console.log(`hasPermission(${permission}): Client permission match found: ${clientPermission}`);
      return true;
    }
    
    // Check for mapped permission (if permission is already client-specific)
    if (permission.startsWith('client_')) {
      const regularPermission = permission.replace('client_', '');
      if (permissions.includes(regularPermission)) {
        console.log(`hasPermission(${permission}): Regular permission match found: ${regularPermission}`);
        return true;
      }
    }
    
    console.log(`hasPermission(${permission}): No match found`);
    return false;
  };

  const hasAnyPermission = (permissionList) => {
    if (!permissions || permissions.length === 0) return false;
    
    // Check for wildcard permission
    if (permissions.includes('*')) return true;
    
    return permissionList.some(permission => hasPermission(permission));
  };

  const hasAllPermissions = (permissionList) => {
    if (!permissions || permissions.length === 0) return false;
    
    // Check for wildcard permission
    if (permissions.includes('*')) return true;
    
    return permissionList.every(permission => hasPermission(permission));
  };

  // Specific permission checks for UI components
  const canViewCandidates = hasPermission('candidates:read') || hasPermission('admin:read');
  const canEditCandidates = hasPermission('candidates:write') || hasPermission('admin:write');
  const canBulkEvaluate = hasPermission('admin:write');
  const canUpdateCandidateRating = hasPermission('candidates:write') || hasPermission('admin:write');
  
  const canViewScreening = hasPermission('screening:read') || hasPermission('admin:read');
  const canEditScreening = hasPermission('screening:write') || hasPermission('admin:write');

  const canViewAdminPanel = hasPermission('admin:read');
  const canEditAdminPanel = hasPermission('admin:write');

  // Debug admin panel permissions
  console.log('🔍 usePermissions - Admin Panel Debug:', {
    canViewAdminPanel,
    canEditAdminPanel,
    hasAdminRead: hasPermission('admin:read'),
    hasAdminWrite: hasPermission('admin:write'),
    permissions
  });

  // User management permissions
  const canManageUsers = hasPermission('admin:write') || hasPermission('users:manage') || hasPermission('client_users:manage') || hasPermission('user_management:write');
  const canCreateUsers = hasPermission('admin:write') || hasPermission('users:manage') || hasPermission('client_users:manage') || hasPermission('user_management:write');
  const canEditUsers = hasPermission('admin:write') || hasPermission('users:manage') || hasPermission('client_users:manage') || hasPermission('user_management:write');
  const canDeleteUsers = hasPermission('admin:write') || hasPermission('users:manage') || hasPermission('client_users:manage') || hasPermission('user_management:write');

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    // Specific permission flags
    canViewCandidates,
    canEditCandidates,
    canBulkEvaluate,
    canUpdateCandidateRating,
    canViewScreening,
    canEditScreening,
    canViewAdminPanel,
    canEditAdminPanel,
    // User management permissions
    canManageUsers,
    canCreateUsers,
    canEditUsers,
    canDeleteUsers
  };
};
